/* eslint-disable react/destructuring-assignment */
/* eslint-disable no-unused-vars */
import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Divider,
  Tooltip,
  Typography,
  Avatar,
  Chip
} from '@material-ui/core';
import {
  Cancel as CancelIcon,
  EventBusy as EventBusyIcon,
  InboxOutlined as InboxIcon
} from '@material-ui/icons';
import { DataGrid } from '@mui/x-data-grid';
import { motion } from 'framer-motion';
import { sessionApi } from '../../../api/sessionApi';
import * as moment from 'moment';
import toast from 'react-hot-toast';
import useMounted from '../../../hooks/useMounted';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, grey } from '../../../theme/tones';

const SessionDesinscriptions = (props) => {
  const { sessionId, mode, ...other } = props;
  const [rows, setRows] = useState([]);
  const mounted = useMounted();
  const navigate = useNavigate();// On utilise navigate afin d'utliser les fonctions de navigation

  const getUnsubscribed = useCallback(async () => {
    try {
      const data = await sessionApi.getUnsubscribed(sessionId, localStorage.getItem('accessToken'));

      if (mounted.current) {
        let status = '';
        if (mode === 'annulation') {
          status = 'Annulation';
        } else if (mode === 'desinscriptions') {
          status = 'À décaler';
        }
        setRows(data.data.unsubscribed.filter((el) => el.status === status).map((customer) => ({ id: customer.id, firstname: customer.customerFirstname, lastname: customer.customerLastname, source: customer.source, unsubscribed_at: customer.unsubscribed_at !== null ? customer.unsubscribed_at : '', unsubscribed_comment: customer.unsubscribed_comment })));
      }
    } catch (err) {
      console.error(err);
      navigate('/404', { replace: true });
    }
  }, [mounted]);

  useEffect(() => {
    getUnsubscribed();
  }, [getUnsubscribed]);

  const handleChange = useCallback(
    async (params) => {
      try {
        await sessionApi.editCustomer(3, params.id, params.field === 'unsubscribed_at' ? moment(params.value).format('YYYY-MM-DD') : params.value, localStorage.getItem('accessToken'), params.field);
      } catch (err) {
        console.error(err);
        toast.error('Il y a eu un souci lors de la modification !');
      }
    }
  );

  const columns = [
    {
      field: 'firstname',
      headerName: 'Prénom',
      width: 300,
    },
    {
      field: 'lastname',
      headerName: 'Nom',
      width: 300,
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 300,
    },
    {
      field: 'unsubscribed_at',
      headerName: 'Date',
      type: 'date',
      renderCell: (params) => moment(params.value).format('DD/MM/YYYY'),
      width: 300,
      editable: true
    },
    {
      field: 'unsubscribed_comment',
      headerName: 'Motif',
      width: 400,
      renderCell: (params) => (
        <Tooltip
          followCursor
          enterDelay={500}
          placement="top"
          title={(<span style={{ whiteSpace: 'pre-line' }}>{params.value}</span>)}
        >
          <span>{params.value}</span>
        </Tooltip>
      ),
      editable: true,
    },
  ];

  const isAnnulation = mode === 'annulation';
  const accentColor = isAnnulation ? getTone('rose').base : getTone('amber').base;
  const accentColorDark = isAnnulation ? getTone('rose').color : getTone('amber').color;
  const accentBg = isAnnulation ? toneAlpha('rose', 0.1) : toneAlpha('amber', 0.1);
  const title = isAnnulation ? 'Motifs Annulations' : 'Motifs Désinscriptions';
  const HeaderIcon = isAnnulation ? CancelIcon : EventBusyIcon;
  const emptyMessage = isAnnulation
    ? 'Aucune annulation enregistrée pour cette session.'
    : 'Aucune désinscription enregistrée pour cette session.';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card
        {...other}
        sx={{
          borderRadius: '20px',
          overflow: 'hidden',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: (theme) => `0 4px 6px -1px ${alpha(theme.palette.common.black, 0.1)}, 0 2px 4px -1px ${alpha(theme.palette.common.black, 0.06)}`,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${theme.palette.background.default} 100%)`,
        }}
      >
        <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.common.white, 0.7), backdropFilter: 'blur(10px)', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColorDark} 100%)`,
                boxShadow: `0 4px 10px -2px ${accentBg}`
              }}
            >
              <HeaderIcon sx={{ color: grey(0) }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>{title}</Typography>
              <Chip
                label={`${rows.length} ${rows.length > 1 ? 'apprenants' : 'apprenant'}`}
                size="small"
                sx={{ mt: 0.5, bgcolor: accentBg, color: accentColor, fontWeight: 800, borderRadius: '8px' }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box
            height={700}
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
                fontFamily: 'inherit',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'transparent',
                borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 800,
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: (theme) => `1px solid ${theme.palette.background.default}`,
                color: 'text.primary',
                fontWeight: 500,
              },
              '& .MuiDataGrid-row': {
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              },
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={100}
              rowsPerPageOptions={[5]}
              onCellEditCommit={handleChange}
              components={{
                NoRowsOverlay: () => (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Avatar sx={{ width: 64, height: 64, bgcolor: accentBg }}>
                      <InboxIcon sx={{ color: accentColor, fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      {emptyMessage}
                    </Typography>
                  </Box>
                ),
              }}
            />
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
};

SessionDesinscriptions.propTypes = {
  sessionId: PropTypes.number.isRequired,
  mode: PropTypes.string.isRequired
};

export default SessionDesinscriptions;
